"use client";

import { useId, useState, type FormEvent } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { Loader2 } from "lucide-react";
import { Button } from "@mozaic/ui/components/button";
import { Input } from "@mozaic/ui/components/input";
import { useQueryClient } from "@tanstack/react-query";
import { classifyAuthError, localLogin, type AuthFailure } from "./api";
import { SsoProviders } from "./sso-providers";

/** Local sign-in uses email and password only. */
export function LoginForm({ next }: { next: string }) {
  const t = useTranslations("auth");
  const router = useRouter();
  const client = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const [pending, setPending] = useState(false);
  const errorId = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setFailure(null);
    try {
      const result = await localLogin({ email: email.trim(), password });
      if (result.status !== "ok") throw new Error("Unexpected login response");
      setPassword("");
      client.clear(); // never reuse cached data from a previous session (frontend architecture §4)
      router.replace(next);
      router.refresh();
    } catch (error) {
      const kind = classifyAuthError(error);
      setFailure(kind);
      setPending(false);
    }
  }

  const invalidCredentials = failure === "credentials";
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight" id="login-heading">
          {t("signInTitle")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("signInSubtitle")}
        </p>
      </header>

      <form onSubmit={submit} aria-labelledby="login-heading" aria-busy={pending} className="space-y-4" noValidate>
        {failure && (
          <p id={errorId} role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {t(`failure.${failure}`)}
          </p>
        )}
        <div className="space-y-1.5">
          <label htmlFor="email" className="text-sm font-medium">
            {t("email")}
          </label>
          <Input
            id="email"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={invalidCredentials}
            aria-describedby={failure ? errorId : undefined}
          />
        </div>
          <div className="space-y-1.5">
            <label htmlFor="password" className="text-sm font-medium">
              {t("password")}
            </label>
            <Input
              id="password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={invalidCredentials}
              aria-describedby={failure ? errorId : undefined}
            />
          </div>
        <div className="flex flex-col gap-2">
          <Button type="submit" disabled={pending || !email || !password}>
            {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {pending ? t("signingIn") : t("signIn")}
          </Button>
        </div>
      </form>

      <SsoProviders />
    </div>
  );
}
